import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { boxApi } from '../utils/api'
import { useAuth } from '../context/AuthContext'

export default function CreateBox() {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [cloneModalOpen, setCloneModalOpen] = useState(false)
  const [adjustModalOpen, setAdjustModalOpen] = useState(false)
  const [boxes, setBoxes] = useState([])
  const [boxesLoading, setBoxesLoading] = useState(false)
  const [boxesError, setBoxesError] = useState(null)
  const [selectedBox, setSelectedBox] = useState(null)
  const [cloneSourceId, setCloneSourceId] = useState(null)
  const todayISO = useMemo(() => new Date().toISOString().split('T')[0], [])
  
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      cycle_duration_months: 6,
      payouts_per_month: 1,
      payout_order: 'join_order',
      start_date: '',
    },
  })

  const loadBoxes = async () => {
    setBoxesLoading(true)
    setBoxesError(null)
    try {
      const data = await boxApi.getAll()
      setBoxes(data || [])
    } catch (err) {
      setBoxesError(err.message)
    } finally {
      setBoxesLoading(false)
    }
  }

  useEffect(() => {
    if (cloneModalOpen && boxes.length === 0 && !boxesLoading) {
      loadBoxes()
    }
  }, [cloneModalOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const ownerBoxes = useMemo(
    () => boxes.filter((box) => box.organizer_id && box.organizer_id === currentUser?.uid),
    [boxes, currentUser?.uid]
  )

  // When a box is selected for cloning, prefill the form values (editable in the adjust modal)
  useEffect(() => {
    if (!selectedBox) return
    setValue('name', `Copy of ${selectedBox.name || 'Box'}`)
    setValue('description', selectedBox.description || '')
    setValue('contribution_amount', selectedBox.contribution_amount ?? '')
    setValue('cycle_duration_months', selectedBox.cycle_duration_months ?? 6)
    setValue('payouts_per_month', selectedBox.payouts_per_month ?? 1)
    setValue('payout_order', selectedBox.payout_order || 'join_order')
    setValue('start_date', todayISO)
  }, [selectedBox, setValue, todayISO])

  const onSubmit = async (data) => {
    try {
      setSubmitting(true)
      setError(null)
      
      const boxData = {
        ...data,
        contribution_amount: parseFloat(data.contribution_amount),
        cycle_duration_months: parseInt(data.cycle_duration_months),
        payouts_per_month: parseInt(data.payouts_per_month || 1),
      }

      const createdBox = cloneSourceId
        ? await boxApi.clone(cloneSourceId, boxData)
        : await boxApi.create(boxData)
      navigate(`/boxes/${createdBox.id}`)
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const applyClone = (box) => {
    if (!box) return
    if (box.organizer_id !== currentUser?.uid) return
    setCloneSourceId(box.id)
    setAdjustModalOpen(false)
    setCloneModalOpen(false)
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Create New Box</h1>
            <p className="mt-2 text-sm text-gray-700">
              Set up a new rotating savings box and invite members to join.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCloneModalOpen(true)}
            className="inline-flex items-center px-4 py-2 rounded-full bg-primary-50 text-primary-700 text-sm font-semibold border border-primary-100 hover:bg-primary-100"
          >
            Clone existing box
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 p-4 rounded-md">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-white shadow-xl rounded-3xl border border-gray-100 p-8">
          <div>
            <label htmlFor="name" className="block text-base font-semibold text-gray-900 mb-2">
              Box Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              {...register('name', { required: 'Box name is required' })}
              className="block w-full px-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="Enter box name"
            />
            {errors.name && (
              <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="block text-base font-semibold text-gray-900 mb-2">
              Description
            </label>
            <textarea
              id="description"
              rows={4}
              {...register('description')}
              className="block w-full px-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
              placeholder="Describe your rotating savings box (optional)"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="contribution_amount" className="block text-base font-semibold text-gray-900 mb-2">
                Monthly Contribution <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-base font-medium">$</span>
                </div>
                <input
                  type="number"
                  id="contribution_amount"
                  step="0.01"
                  {...register('contribution_amount', {
                    required: 'Contribution amount is required',
                    min: { value: 0.01, message: 'Must be greater than 0' },
                  })}
                  className="block w-full pl-8 pr-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="0.00"
                />
              </div>
              {errors.contribution_amount && (
                <p className="mt-2 text-sm text-red-600">{errors.contribution_amount.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="cycle_duration_months" className="block text-base font-semibold text-gray-900 mb-2">
                Cycle Duration (months) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="cycle_duration_months"
                {...register('cycle_duration_months', {
                  required: 'Cycle duration is required',
                  min: { value: 1, message: 'Must be at least 1 month' },
                })}
                className="block w-full px-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="6"
              />
              {errors.cycle_duration_months && (
                <p className="mt-2 text-sm text-red-600">{errors.cycle_duration_months.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="payouts_per_month" className="block text-base font-semibold text-gray-900 mb-2">
                Payouts per Month <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="payouts_per_month"
                {...register('payouts_per_month', {
                  required: 'Payouts per month is required',
                  min: { value: 1, message: 'Must be at least 1' },
                })}
                className="block w-full px-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="1"
              />
              {errors.payouts_per_month && (
                <p className="mt-2 text-sm text-red-600">{errors.payouts_per_month.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="start_date" className="block text-base font-semibold text-gray-900 mb-2">
              Start Date
            </label>
            <input
              type="date"
              id="start_date"
              {...register('start_date')}
              className="block w-full px-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-base font-semibold text-gray-900 mb-3">
              Payout Order
            </label>
            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  type="radio"
                  id="join_order"
                  value="join_order"
                  {...register('payout_order')}
                  className="mt-1 h-5 w-5 text-primary-600 border-gray-300 focus:ring-primary-500 focus:ring-2 cursor-pointer"
                />
                <label htmlFor="join_order" className="ml-3 block text-base text-gray-700 cursor-pointer">
                  <span className="font-medium">Join Order (default)</span>
                  <span className="block text-sm text-gray-500 mt-1">Members receive payouts in the order they join.</span>
                </label>
              </div>
              <div className="flex items-start">
                <input
                  type="radio"
                  id="custom_order"
                  value="custom"
                  {...register('payout_order')}
                  className="mt-1 h-5 w-5 text-primary-600 border-gray-300 focus:ring-primary-500 focus:ring-2 cursor-pointer"
                />
                <label htmlFor="custom_order" className="ml-3 block text-base text-gray-700 cursor-pointer">
                  <span className="font-medium">Custom Order</span>
                  <span className="block text-sm text-gray-500 mt-1">Set a specific rotation after members join.</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate('/boxes')}
              className="px-6 py-3 border border-gray-200 rounded-full shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 border border-transparent rounded-full shadow-sm text-base font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Creating...' : 'Create Box'}
            </button>
          </div>
        </form>
      </div>

      {/* Clone picker modal */}
      <Modal
        open={cloneModalOpen}
        onClose={() => setCloneModalOpen(false)}
        title="Clone an existing box"
      >
        {boxesError && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {boxesError}
          </div>
        )}
        {boxesLoading ? (
          <p className="text-sm text-gray-600">Loading your boxes...</p>
        ) : ownerBoxes.length === 0 ? (
          <p className="text-sm text-gray-600">No boxes you organize are available to clone.</p>
        ) : (
          <div className="space-y-3">
            {ownerBoxes.map((box) => (
              <button
                key={box.id}
                className={`w-full text-left rounded-2xl border px-4 py-3 text-sm hover:border-primary-300 hover:bg-primary-50 ${
                  selectedBox?.id === box.id ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-white'
                }`}
                onClick={() => {
                  if (box.organizer_id !== currentUser?.uid) return
                  setSelectedBox(box)
                  setAdjustModalOpen(true)
                }}
              >
                <p className="font-semibold text-gray-900">{box.name}</p>
                <p className="text-xs text-gray-500">
                  {box.cycle_duration_months} mo · {box.payouts_per_month} payout(s)/mo · ${box.contribution_amount} per member
                </p>
              </button>
            ))}
          </div>
        )}
      </Modal>

      {/* Adjust cloned settings modal */}
      <Modal
        open={adjustModalOpen}
        onClose={() => setAdjustModalOpen(false)}
        title="Adjust cloned box settings"
      >
        {selectedBox ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Review or tweak the copied values. Start date defaults to today so the schedule moves forward.
            </p>
            <div className="grid grid-cols-1 gap-3">
              <label className="text-sm font-semibold text-gray-800">
                Name
                <input
                  type="text"
                  value={watch('name') || ''}
                  onChange={(e) => setValue('name', e.target.value)}
                  className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                />
              </label>
              <label className="text-sm font-semibold text-gray-800">
                Start date
                <input
                  type="date"
                  value={watch('start_date') || todayISO}
                  onChange={(e) => setValue('start_date', e.target.value)}
                  className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                />
              </label>
              <label className="text-sm font-semibold text-gray-800">
                Contribution
                <input
                  type="number"
                  step="0.01"
                  value={watch('contribution_amount') ?? ''}
                  onChange={(e) => setValue('contribution_amount', e.target.value)}
                  className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                />
              </label>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setAdjustModalOpen(false)}
                className="px-4 py-2 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => applyClone(selectedBox)}
                className="px-4 py-2 rounded-full bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
              >
                Use these values
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-600">Select a box to clone first.</p>
        )}
      </Modal>
    </div>
  )
}

function Modal({ open, onClose, children, title }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl border border-gray-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  )
}
